import React, { useEffect, useState } from "react";
import { PlusOutlined } from "@ant-design/icons";
import Button from "../../Helpers/Button/Button";
import trans from "../../../utils";
import { useFormikContext } from "formik";
import {
	useGetPOIProjectsQuery,
	useGetPOIZonesByParentIdQuery,
	useGetPOIBuildingsQuery,
} from "../../../Redux/service/POIS/POIs";
import {
	useGetSectorsQuery,
	useGetContractorsQuery,
	useGetOptionsQuery,
	useGetFloorplanQuery,
} from "../../../Redux/service/Snag/Snag";
import SnagItemRow from "./SnagItemRow";
import { InputSelect } from "../../Helpers/Input/RefactoredInput";
import { Input } from "../../Helpers/Input/Input";
import SnagLocationSelectorModal from "../../snagLocation/SnagLocationSelectorModal";

export default function SnagFormBody({
	preselectedLocation = null,
	isEditMode = false,
}) {
	const {
		values,
		errors,
		touched,
		handleChange,
		handleBlur,
		setFieldValue,
		setFieldTouched,
	} = useFormikContext();
	// ✅ Check if location fields should be disabled (both preselected AND edit mode)
	const isLocationPreselected = !!preselectedLocation || isEditMode;
	const [showLocationModal, setShowLocationModal] = useState(false);

	console.log("🔍 SnagFormBody Debug:");
	console.log("isEditMode:", isEditMode);
	console.log("preselectedLocation:", preselectedLocation);
	console.log("isLocationPreselected:", isLocationPreselected);
	console.log("values.project_id:", values.project_id);
	console.log("values.zone_id:", values.zone_id);
	console.log("values.building_id:", values.building_id);
	console.log("values.level:", values.level);
	console.log(
		"values.snags[0]?.assigned_team_id:",
		values.snags?.[0]?.assigned_team_id,
	);

	// Fetch projects (always available)
	const { data: projectsData } = useGetPOIProjectsQuery();

	// Fetch zones based on selected project
	const { data: zonesData, isFetching: zonesLoading } =
		useGetPOIZonesByParentIdQuery(
			{ id: values.project_id },
			{
				skip: !values.project_id,
			},
		);

	// Fetch buildings based on selected zone
	const { data: buildingsData, isFetching: buildingsLoading } =
		useGetPOIBuildingsQuery(
			{ id: values.zone_id },
			{
				skip: !values.zone_id,
			},
		);

	// Fetch spaces from building map when building is selected (using poiId)
	const { data: buildingMapData, isFetching: buildingMapLoading } =
		useGetFloorplanQuery(values.building_id, {
			skip: !values.building_id,
		});

	// Fetch options for levels and severities
	const { data: optionsData } = useGetOptionsQuery();

	// Fetch contractors based on selected project
	const { data: contractorsData, isFetching: contractorsLoading } =
		useGetContractorsQuery(values.project_id, {
			skip: !values.project_id,
		});

	const projectsOptions =
		projectsData?.data?.map((project) => ({
			label: project.name,
			value: project.id,
		})) || [];

	const zonesOptions =
		zonesData?.data?.map((zone) => ({
			label: zone.name,
			value: zone.id,
		})) || [];

	const buildingsOptions =
		buildingsData?.data?.map((building) => ({
			label: building.name,
			value: building.id,
		})) || [];

	const levelsOptions = optionsData?.data?.levels || [];
	const severitiesOptions = optionsData?.data?.severities || [];

	// Prepare spaces options from building map data (using poiId as value)
	const spacesOptions =
		buildingMapData?.data?.rooms?.map((room) => ({
			label: room.displayName || room.code,
			value: room.poiId, // Use poiId instead of code
			code: room.code,
			name: room.name,
		})) || [];

	// Get display names for preselected location (use form values or stored names)
	const getProjectName = () => {
		const projectId = values.project_id || preselectedLocation?.project_id;
		if (!projectId) return "";
		// Use stored name if available, otherwise find from options
		if (preselectedLocation?.project_name)
			return preselectedLocation.project_name;
		const project = projectsOptions.find((p) => p.value === projectId);
		return project?.label || "";
	};

	const getZoneName = () => {
		const zoneId = values.zone_id || preselectedLocation?.zone_id;
		if (!zoneId) return "";
		// Use stored name if available, otherwise find from options
		if (preselectedLocation?.zone_name) return preselectedLocation.zone_name;
		const zone = zonesOptions.find((z) => z.value === zoneId);
		return zone?.label || "";
	};

	const getBuildingName = () => {
		const buildingId = values.building_id || preselectedLocation?.building_id;
		if (!buildingId) return "";
		// Use stored name if available, otherwise find from options
		if (preselectedLocation?.building_name)
			return preselectedLocation.building_name;
		const building = buildingsOptions.find((b) => b.value === buildingId);
		return building?.label || "";
	};

	const getSpaceName = () => {
		const levelId = values.level || preselectedLocation?.level;
		if (!levelId) return "";
		// Use stored name if available, otherwise find from map data
		if (preselectedLocation?.space_name) return preselectedLocation.space_name;
		const space = buildingMapData?.data?.rooms?.find(
			(r) => r.poiId === levelId,
		);
		return space?.displayName || space?.code || "";
	};

	// Handle location change - update form values and store names for display
	const handleLocationChange = (locationData) => {
		console.log("=== MODAL SENT THIS DATA ===");
		console.log("Full locationData:", JSON.stringify(locationData, null, 2));
		console.log("- project_id:", locationData.project_id);
		console.log("- zone_id (Building):", locationData.zone_id);
		console.log("- building_id (Level):", locationData.building_id);
		console.log("- level (Space):", locationData.level);
		console.log("============================");

		setFieldValue("project_id", locationData.project_id);
		setFieldValue("zone_id", locationData.zone_id);
		setFieldValue("building_id", locationData.building_id);
		setFieldValue("level", locationData.level); // This is now poiId

		if (values.snags?.[0]) {
			setFieldValue("snags[0].area_room", locationData.area_room);
			setFieldValue("snags[0].location", locationData.location);
		}

		console.log("=== FORM VALUES AFTER SET ===");
		setTimeout(() => {
			console.log("values.project_id:", values.project_id);
			console.log("values.zone_id:", values.zone_id);
			console.log("values.building_id:", values.building_id);
			console.log("values.level:", values.level);
		}, 100);
		console.log("==============================");

		setShowLocationModal(false);
	};

	const contractorsList = contractorsData?.data || contractorsData || [];
	const contractorsOptions =
		contractorsList.map((contractor) => ({
			label: contractor.name || contractor.label || contractor,
			value: contractor.id || contractor.value || contractor,
		})) || [];

	// ✅ FIXED: Reset dependent fields when parent changes
	// Don't reset if values came from preselected location (modal) OR edit mode
	useEffect(() => {
		// Don't reset if we're working with preselected location or edit mode
		if (preselectedLocation || isEditMode) return;

		if (values.project_id) {
			setFieldValue("zone_id", "");
			setFieldValue("building_id", "");
			// Reset assigned_team_id for all snags when project changes
			const updatedSnags = (values.snags || []).map((snag) => ({
				...snag,
				assigned_team_id: "",
			}));
			if (updatedSnags.length > 0) {
				setFieldValue("snags", updatedSnags);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [values.project_id]);

	// ✅ FIXED: Don't reset building_id if we have preselected location or edit mode
	useEffect(() => {
		// Don't reset if we're working with preselected location or edit mode
		if (preselectedLocation || isEditMode) return;

		if (!values.zone_id) {
			setFieldValue("building_id", "");
		}
	}, [values.zone_id, setFieldValue, preselectedLocation, isEditMode]);

	const snags = values.snags || [];

	const addSnag = () => {
		const newSnags = [
			...snags,
			{
				sector: "",
				assigned_team_id: "",
				subsystem: "",
				discipline: "",
				item: "",
				description: "",
				asset_tag_id: "",
				drawing_ref: "",
				area_room: "",
				location: "",
				severity: "",
				acceptance_criteria: "",
				inspection_method: "",
				location_details: "",
				target_date: "",
				files: [],
			},
		];
		setFieldValue("snags", newSnags);
	};

	const updateSnag = (index, field, value) => {
		const updatedSnags = [...snags];
		updatedSnags[index][field] = value;
		setFieldValue("snags", updatedSnags);
	};

	const removeSnag = (index) => {
		const updatedSnags = snags.filter((_, i) => i !== index);
		setFieldValue("snags", updatedSnags);
	};

	const handleFileChange = (index, files) => {
		updateSnag(index, "files", files || []);
	};

	// Fetch sectors (always available)
	const { data: sectorsData } = useGetSectorsQuery();

	const sectorsOptions =
		sectorsData?.data?.map((sector) => ({
			label: sector.label,
			value: sector.code,
		})) || [];

	return (
		<div className="form-section">
			{isLocationPreselected ? (
				<>
					<div className="row">
						<div className="col-lg-6">
							<Input
								title={trans("Project")}
								type="text"
								formstyle="form-input"
								id="project_id_display"
								name="project_id_display"
								value={getProjectName()}
								disabled={true}
							/>
						</div>
						<div className="col-lg-6">
							<Input
								title={trans("Building")}
								type="text"
								formstyle="form-input"
								id="zone_id_display"
								name="zone_id_display"
								value={getZoneName()}
								disabled={true}
							/>
						</div>
					</div>
					<div className="row">
						<div className="col-lg-6">
							<Input
								title={trans("Level")}
								type="text"
								formstyle="form-input"
								id="building_id_display"
								name="building_id_display"
								value={getBuildingName()}
								disabled={true}
							/>
						</div>
						<div className="col-lg-6">
							<Input
								title={trans("Space")}
								type="text"
								formstyle="form-input"
								id="level_display"
								name="level_display"
								value={getSpaceName()}
								disabled={true}
							/>
						</div>
					</div>
					{/* ✅ FIXED: Show "Change Location" button whenever location is preselected (both edit and create from map) */}
					{isLocationPreselected && (
						<div className="row mt-3">
							<div className="col-12">
								<Button
									type="default"
									onClick={() => setShowLocationModal(true)}
								>
									{trans("Change Location")}
								</Button>
							</div>
						</div>
					)}
					{/* Hidden fields to store actual IDs for submission */}
					<input type="hidden" name="project_id" value={values.project_id} />
					<input type="hidden" name="zone_id" value={values.zone_id} />
					<input type="hidden" name="building_id" value={values.building_id} />
					<input type="hidden" name="level" value={values.level} />
				</>
			) : (
				<>
					<div className="row">
						{/* Static Fields */}
						<div className="col-lg-6 select-z-index">
							<InputSelect
								id="project_id"
								name="project_id"
								label={trans("Project")}
								placeholder={trans("Select Project")}
								options={projectsOptions}
								value={values.project_id}
								onChange={(val) => {
									setFieldValue("project_id", val);
									setFieldValue("zone_id", "");
									setFieldValue("building_id", "");
								}}
								onBlur={() => setFieldTouched("project_id", true)}
								errors={errors.project_id}
								touched={touched.project_id}
								formstyle="form-input"
							/>
						</div>
						<div className="col-lg-6 select-z-index">
							<InputSelect
								id="zone_id"
								name="zone_id"
								label={trans("Building")}
								placeholder={trans("Select Building")}
								options={zonesOptions}
								value={values.zone_id}
								onChange={(val) => {
									setFieldValue("zone_id", val);
									setFieldValue("building_id", "");
								}}
								onBlur={() => setFieldTouched("zone_id", true)}
								errors={errors.zone_id}
								touched={touched.zone_id}
								formstyle="form-input"
								disabled={!values.project_id}
								loading={zonesLoading}
							/>
						</div>
					</div>
					<div className="row">
						<div className="col-lg-6 select-z-index">
							<InputSelect
								id="building_id"
								name="building_id"
								label={trans("Level")}
								placeholder={trans("Select Level")}
								options={buildingsOptions}
								value={values.building_id}
								onChange={(val) => setFieldValue("building_id", val)}
								onBlur={() => setFieldTouched("building_id", true)}
								errors={errors.building_id}
								touched={touched.building_id}
								formstyle="form-input"
								disabled={!values.zone_id}
								loading={buildingsLoading}
							/>
						</div>
						<div className="col-lg-6 select-z-index">
							<InputSelect
								id="level"
								name="level"
								label={trans("Space")}
								placeholder={trans("Select Space")}
								options={spacesOptions}
								value={values.level}
								onChange={(val) => setFieldValue("level", val)}
								onBlur={() => setFieldTouched("level", true)}
								errors={errors.level}
								touched={touched.level}
								formstyle="form-input"
								disabled={!values.building_id}
								loading={buildingMapLoading}
							/>
						</div>
					</div>
				</>
			)}

			{/* Location Selector Modal */}
			<SnagLocationSelectorModal
				open={showLocationModal}
				onClose={() => setShowLocationModal(false)}
				onConfirm={handleLocationChange}
				preselectedValues={{
					project_id: values.project_id,
					zone_id: values.zone_id,
					building_id: values.building_id,
				}}
			/>

			{/* Snags Array Section */}
			<div className="snags-section mt-4">
				<div className="d-flex justify-content-between align-items-center mb-3">
					<h5 className="mb-0">{trans("Snags")}</h5>
				</div>

				{snags.map((snag, index) => {
					const snagErrors = errors.snags?.[index] || {};
					const snagTouched = touched.snags?.[index] || {};

					return (
						<SnagItemRow
							key={index}
							snag={snag}
							index={index}
							updateSnag={updateSnag}
							removeSnag={removeSnag}
							handleFileChange={handleFileChange}
							snagErrors={snagErrors}
							snagTouched={snagTouched}
							sectorsOptions={sectorsOptions}
							contractorsOptions={contractorsOptions}
							severitiesOptions={severitiesOptions}
							contractorsLoading={contractorsLoading}
							totalSnags={snags.length}
							preselectedLocation={preselectedLocation}
							isEditMode={isEditMode}
						/>
					);
				})}
				{/* ✅ Hide "Add Another Snag" button in edit mode */}
				{!isEditMode && (
					<div className="mt-3 d-flex justify-content-center">
						<Button type="default" icon={<PlusOutlined />} onClick={addSnag}>
							{trans("Add Another Snag")}
						</Button>
					</div>
				)}
			</div>
		</div>
	);
}
